import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const TestingHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="t-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="t-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#t-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* test runner panel */}
    <rect x="80" y="100" width="300" height="260" rx="18" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" />
    <rect x="100" y="120" width="120" height="10" rx="3" fill="rgba(0,212,255,0.55)" />

    {/* passing test rows */}
    <g fontFamily="JetBrains Mono, monospace" fontSize="12">
      <g>
        <circle cx="110" cy="160" r="6" fill="#00c851" />
        <text x="124" y="164" fill="rgba(255,255,255,0.85)">login.spec.ts</text>
        <text x="350" y="164" textAnchor="end" fill="#00c851">PASS</text>
      </g>
      <g>
        <circle cx="110" cy="186" r="6" fill="#00c851" />
        <text x="124" y="190" fill="rgba(255,255,255,0.85)">checkout.spec.ts</text>
        <text x="350" y="190" textAnchor="end" fill="#00c851">PASS</text>
      </g>
      <g>
        <circle cx="110" cy="212" r="6" fill="#00c851" />
        <text x="124" y="216" fill="rgba(255,255,255,0.85)">payment.spec.ts</text>
        <text x="350" y="216" textAnchor="end" fill="#00c851">PASS</text>
      </g>
      <g>
        <circle cx="110" cy="238" r="6" fill="#ffbb33">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="1.6s" repeatCount="indefinite" />
        </circle>
        <text x="124" y="242" fill="rgba(255,255,255,0.85)">api.contract.spec.ts</text>
        <text x="350" y="242" textAnchor="end" fill="#ffbb33">RUN</text>
      </g>
      <g>
        <circle cx="110" cy="264" r="6" fill="rgba(255,255,255,0.18)" />
        <text x="124" y="268" fill="rgba(255,255,255,0.45)">auth.e2e.spec.ts</text>
        <text x="350" y="268" textAnchor="end" fill="rgba(255,255,255,0.45)">QUEUE</text>
      </g>
    </g>

    {/* progress */}
    <rect x="100" y="298" width="260" height="8" rx="4" fill="rgba(255,255,255,0.10)" />
    <rect x="100" y="298" width="180" height="8" rx="4" fill="url(#t-grad)">
      <animate attributeName="width" values="40;220;40" dur="5s" repeatCount="indefinite" />
    </rect>
    <text x="100" y="332" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(255,255,255,0.55)">214 / 240 passing</text>

    {/* floating tick badge */}
    <g>
      <circle cx="395" cy="95" r="22" fill="url(#t-grad)" />
      <path d="M385 96 l8 8 14 -16" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -10;0 0" dur="5s" repeatCount="indefinite" />
    </g>
  </svg>
)

const SoftwareTesting = () => (
  <ServicePage
    slug="software-testing"
    seo={{
      title: 'Software Testing & QA Services',
      description:
        'Comprehensive software testing services from QA Tech — automated, manual, performance, accessibility and security testing. Reduce defects, ship faster and protect your brand.',
      keywords:
        'software testing services, QA services, test automation, performance testing, manual testing, accessibility testing, security testing, regression testing, end-to-end testing, UK QA consultancy',
    }}
    hero={{
      eyebrow: 'Service / Software Testing',
      title: 'Catch bugs before your customers do.',
      lede:
        'Automated, manual, performance, accessibility and security testing — engineered to keep release notes short and customer reviews glowing.',
    }}
    visual={<TestingHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="Test Strategy"
      title="Risk first. Coverage second."
    >
      <p>
        Testing everything looks impressive on paper and runs out of money on
        Sprint 4. We build a risk-driven test strategy &mdash; what hurts most
        if it breaks, what rarely changes, what runs hot at month-end.
      </p>
      <p>
        The result: a fast, focused suite that protects the business, not a
        bloated one that ages into a maintenance burden.
      </p>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Test Automation"
      title="Automation that doesn't flake."
      accent={
        <span className="sp-accent-stat">
          <span className="sp-accent-stat__num">~92%</span>
          <span className="sp-accent-stat__label">avg automation coverage</span>
        </span>
      }
    >
      <p>
        We build automation that engineers actually trust. Stable selectors,
        proper waits, parallel execution, retry strategies and a clean Page
        Object architecture. Cypress, Playwright, WebdriverIO, REST Assured,
        Pact &mdash; we use the right tool for your stack, not the loudest.
      </p>
      <ul>
        <li>End-to-end &amp; component testing</li>
        <li>API &amp; contract testing</li>
        <li>Cross-browser &amp; device matrices</li>
        <li>Visual regression &amp; snapshot testing</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="Performance"
      title="Find the cliff before you fall off it."
      accent={
        <div className="sp-accent-bars" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
      }
    >
      <p>
        Load, stress, soak and spike testing &mdash; modelled against real
        user behaviour, not generic scripts. We instrument backend services,
        run controlled tests and produce reports your engineers can act on:
        bottlenecks, hot queries, memory leaks, scaling thresholds.
      </p>
      <ul>
        <li>k6, JMeter, Gatling and Locust</li>
        <li>Realistic scenario modelling</li>
        <li>Backend metrics correlated with user-perceived latency</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Accessibility"
      title="Inclusive by default."
    >
      <p>
        Accessibility is non-negotiable, both ethically and legally. We test
        against WCAG 2.2 AA using a mix of automated tools (axe, Lighthouse,
        Pa11y) and human screen-reader testing &mdash; because a clean axe
        report is not the same as a usable product.
      </p>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="Security"
      title="Test like an attacker would."
    >
      <p>
        OWASP Top 10, dependency scanning, secrets detection, authentication
        and authorisation testing, fuzzing on critical inputs. We integrate
        DAST/SAST tooling into CI so vulnerabilities are caught early and
        cheap, instead of late and expensive.
      </p>
    </SplitSection>

    <SplitSection
      index={6}
      align="right"
      kicker="Reporting"
      title="Test results your CTO will read."
    >
      <p>
        No 87-tab spreadsheets. We deliver clean dashboards, trend lines and
        executive summaries that explain the <strong>so what</strong>: which
        risks are down, which are up, and what to do next.
      </p>
    </SplitSection>

    <StackRibbon
      label="Tools we use"
      items={[
        'Playwright', 'Cypress', 'WebdriverIO', 'Selenium',
        'Cucumber', 'Jest', 'Vitest', 'Mocha',
        'k6', 'JMeter', 'Gatling',
        'axe', 'Pa11y', 'Lighthouse', 'OWASP ZAP',
        'Postman', 'REST Assured', 'Pact',
      ]}
    />
  </ServicePage>
)

export default SoftwareTesting
